import React from 'react';

const SuccessionPlanningFormField = ({ 
  type = 'text', 
  label, 
  value, 
  onChange, 
  placeholder, 
  required = false, 
  options = [], 
  rows = 3,
  className = '',
  error = null 
}) => {
  const fieldId = `field-${label?.toLowerCase().replace(/\s+/g, '-')}`;
  
  const baseInputClasses = `
    w-full px-3 py-2 
    bg-succession-bg-primary 
    text-succession-text-primary 
    border border-succession-border 
    rounded-md 
    focus:outline-none 
    focus:ring-2 
    focus:ring-succession-primary 
    focus:border-succession-primary 
    transition-all duration-300
    placeholder:text-succession-text-muted
  `;
  
  const errorClasses = error ? 'border-succession-error focus:ring-succession-error focus:border-succession-error' : '';
  
  const renderField = () => {
    switch (type) {
      case 'select':
        return (
          <select
            id={fieldId}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            className={`${baseInputClasses} ${errorClasses} ${className}`}
            required={required}
          >
            <option value="">Select an option...</option>
            {options.map((option, index) => (
              <option key={index} value={option.value || option}>
                {option.label || option}
              </option>
            ))}
          </select>
        );
      
      case 'textarea':
        return (
          <textarea
            id={fieldId}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            rows={rows}
            className={`${baseInputClasses} ${errorClasses} ${className} resize-none`}
            required={required}
          />
        );
      
      case 'radio':
        return (
          <div className="space-y-3">
            {options.map((option, index) => (
              <label key={index} className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name={fieldId}
                  value={option.value || option}
                  checked={value === (option.value || option)}
                  onChange={(e) => onChange(e.target.value)}
                  className="mr-3 h-4 w-4 text-succession-primary border-succession-border focus:ring-succession-primary transition-colors duration-300"
                  required={required}
                />
                <span className="text-sm text-succession-text-primary transition-colors duration-300">
                  {option.label || option}
                </span>
              </label>
            ))}
          </div>
        );
      
      case 'checkbox':
        return (
          <div className="flex items-center">
            <input
              type="checkbox"
              id={fieldId}
              checked={value || false}
              onChange={(e) => onChange(e.target.checked)}
              className="h-4 w-4 text-succession-primary border-succession-border rounded focus:ring-succession-primary transition-colors duration-300"
              required={required}
            />
            <label htmlFor={fieldId} className="ml-3 text-sm text-succession-text-primary cursor-pointer transition-colors duration-300">
              {label}
            </label>
          </div>
        );
      
      case 'number':
        return (
          <input
            type="number"
            id={fieldId}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className={`${baseInputClasses} ${errorClasses} ${className}`}
            required={required}
          />
        );
      
      default:
        return (
          <input
            type={type}
            id={fieldId}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className={`${baseInputClasses} ${errorClasses} ${className}`}
            required={required}
          />
        );
    }
  };

  if (type === 'checkbox') {
    return (
      <div className="mb-4">
        {renderField()}
        {error && (
          <p className="mt-1 text-sm text-succession-error transition-colors duration-300">
            {error}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="mb-4">
      {label && (
        <label 
          htmlFor={fieldId} 
          className="block text-sm font-medium text-succession-text-primary mb-2 transition-colors duration-300"
        >
          {label}
          {required && <span className="text-succession-error ml-1">*</span>}
        </label>
      )}
      {renderField()}
      {error && (
        <p className="mt-1 text-sm text-succession-error transition-colors duration-300">
          {error}
        </p>
      )}
    </div>
  );
};

export default SuccessionPlanningFormField; 